import { createFeatureSelector, createSelector } from '@ngrx/store';
import { EventFilter, IEvent } from 'src/app/core/models/event'; // Adjust the import path accordingly
import { selectLoadingInitialGuardian } from '../guardian/guardian.selectors';
import { selectAllStudents } from '../students/student.selectors';
import { EventState } from './event.reducer';

// Feature selector for the event state
export const selectEventState = createFeatureSelector<EventState>('events');

// Selector to get all events
export const selectAllEvents = createSelector(selectEventState, (state: EventState) => state.events);

export const selectSelectedEventId = createSelector(selectEventState, (state: EventState) => state.selectedEventId);

export const selectSelectedEvent = createSelector(
  selectAllEvents,
  selectSelectedEventId,
  (events, id) => events.find((e) => e.id === id) ?? null,
);

export const selectEventFilters = createSelector(selectEventState, (state: EventState) => state.filters);

// Selector to get events for a specific month
export const selectEventsForMonth = (month: number) =>
  createSelector(selectAllEvents, (events: IEvent[]) =>
    events.filter((event) => new Date(event.start).getMonth() + 1 === month),
  );

// Selector to get an event by ID
export const selectEventById = (eventId: string) =>
  createSelector(selectAllEvents, (events: IEvent[]) => events.find((event) => event.id === eventId) ?? null);

// Selector to get a specific number of upcoming events
export const selectUpcomingEvents = (amount: number) =>
  createSelector(selectAllFilteredEvents, (events: IEvent[]) => {
    const upcomingEvents = events.filter((event) => new Date(event.start) > new Date());
    return upcomingEvents.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()).slice(0, amount);
  });

export const selectEventLoadingStatus = createSelector(
  selectEventState,
  selectLoadingInitialGuardian,
  (state: EventState, loadingGuardian) => state.loading || loadingGuardian,
);

// Selector to get all filtered events
export const selectAllFilteredEvents = createSelector(
  selectAllEvents,
  selectEventFilters,
  selectAllStudents,
  (events: IEvent[], filters: EventFilter, students) => {
    return events.filter((event) => {
      // Determine if event is free or paid based on event type
      // Academic events are typically free, Extracurricular events are typically paid
      const isFreeEvent = event.type === 'free' || event.type === 'Free';
      const isPaidEvent = event.type === 'paid' || event.type === 'Paid';

      // Filter based on free events
      if (!filters.free && isFreeEvent) {
        return false;
      }

      // Filter based on paid events
      if (!filters.paid && isPaidEvent) {
        return false;
      }

      if (filters.studentColours.length > 0) {
        // Check if ALL of the event's student IDs have colors that should be filtered out
        const allStudentsFiltered =
          event.studentIds?.every((studentId) => {
            const student = students.find((s) => s.id === studentId);
            return student && filters.studentColours.includes(student.colour);
          }) || false;
        if (allStudentsFiltered && event.studentIds && event.studentIds.length > 0) {
          return false;
        }
      }

      // Filter based on school names (if the school name is filtered out, don't display the event)
      if (filters.schoolIds.length > 0 && filters.schoolIds.includes(event.location?.school ?? '')) {
        return false;
      }

      return true;
    });
  },
);

export const selectAllSchoolNamesFromEvents = createSelector(selectAllEvents, (events: IEvent[]) => {
  const schoolNames = new Set<string>();
  events.forEach((event) => {
    if (event.relatedField?.type === 'School' && event.location?.school && !schoolNames.has(event.location.school)) {
      schoolNames.add(event.location.school);
    }
  });
  return Array.from(schoolNames);
});

// Selector to get the appropriate color for an event based on its student IDs
export const selectEventColor = (event: IEvent) =>
  createSelector(selectAllStudents, (students) => {
    const MULTI_STUDENT_COLOR = '#14448F';
    const DEFAULT_COLOR = '#14448F';

    if (!event.studentIds || event.studentIds.length === 0) {
      return DEFAULT_COLOR;
    }

    // If multiple students are involved, return blue
    if (event.studentIds.length > 1) {
      return MULTI_STUDENT_COLOR;
    }

    // For single student, get their specific color
    const student = students.find((s) => s.id === event.studentIds![0]);
    return student?.colour || DEFAULT_COLOR;
  });
